import { CommentAPI } from "@griffingroupglobal/eslib-api";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useEditCalendar } from "../../../../hooks/api/calendar";
import { useEditTaskList } from "../../../../hooks/api/tasks";
import useCurrentUser from "../../../../hooks/useCurrentUser";
import useFilesPost from "../../../../hooks/useFilesPost";
import { useAppState } from "../../../../state/appState";
import { toastError, toastMessage } from "../../Toast/Toast";
import taskKeys from "../../../../hooks/api/tasks/taskKeys";

const useMarkCompletePopupData = ({ popupData, closePopup }) => {
  // local values
  const { name } = popupData?.item;
  const [duration, setDuration] = useState();
  const [timeUnit, setTimeUnit] = useState({
    label: "Day(s)",
    value: "calendarDay",
  });

  const queryClient = useQueryClient();

  const { editTaskList } = useEditTaskList();

  // Check to see if ALL items of resource are checked
  const initialStatus = (resource) =>
    popupData?.item?.[resource]?.every((item) => item.isCompleted);

  const initialState = {
    spaces: initialStatus("spaces"),
    assets: initialStatus("assets"),
    steps: initialStatus("steps"),
  };

  // Checkbox of ALL steps true = all completed false = at least one not completed
  const [completeStatus, setCompleteStatus] = useState(initialState);

  // If All steps completed show new text
  const checkBoxText = {
    spaces: completeStatus.spaces ? "All Spaces Completed" : "Check All Spaces",
    assets: completeStatus.assets ? "All Assets Completed" : "Check All Assets",
    steps: completeStatus.steps ? "All Steps Completed" : "Check All Steps",
  };

  const [comment, setComment] = useState("");
  const [filesToAdd, setFilesToAdd] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const { editInCalendar } = useEditCalendar();
  // custom hooks
  const { data: currentUser } = useCurrentUser();
  const [{ sopsDict }] = useAppState();
  const handleFilesAdded = (files) => {
    setFilesToAdd(files);
  };

  const { mutate: postFiles } = useFilesPost();

  const handleRemoveAttachment = (fileId) => {
    const filteredFiles = filesToAdd.filter((f) => f.id !== fileId);
    setFilesToAdd(filteredFiles);
  };

  const postFilesPromise = (files) => {
    return new Promise((resolve, reject) => {
      postFiles(files, {
        onSuccess: (response) => {
          resolve(response);
        },
        onError: (error) => {
          reject(error);
        },
      });
    });
  };

  const handleDurationChange = (val) => {
    if (val === "0" || val === "") setDuration(val);
    else {
      // Remove leading zeros
      const formattedDuration = val?.replace(/^0+/, "");
      setDuration(formattedDuration);
    }
  };

  // Check if Steps were Marked Complete in Popup
  const updatedSteps = useCallback(
    (originalTask) => {
      const copyOfTask = cloneDeep(originalTask);
      // if all original steps were not complete and checkbox selected
      const stepArray = [];
      copyOfTask?.steps?.forEach((step) => {
        if (!step?.isCompleted) {
          const newStep = step;
          newStep.isCompleted = true;
          if (step.sop) {
            // update sop individual steps
            const sopResource = sopsDict[step.sop];
            newStep.sopStepStatus = sopResource?.steps?.map((sopStep) => {
              return {
                id: sopStep.id,
                name: sopStep.description,
                isCompleted: true,
              };
            });
          }

          return stepArray.push({ ...newStep });
        }
        return stepArray.push({ ...step });
      });
      return stepArray;
    },
    [sopsDict]
  );

  // Check if all items of resource were Marked Complete in Popup
  const updatedResourceItems = (originalTask, resource) => {
    return originalTask?.[resource]?.map((item) => {
      if (!item?.isCompleted) {
        return { ...item, isCompleted: true };
      }
      return { ...item };
    });
  };

  const disableSubmit = useMemo(() => {
    // disable submit button if duration value i negative or component is in saving status
    return (
      duration === "" ||
      duration === undefined ||
      Number(duration) < 0 ||
      isSaving
    );
  }, [duration, isSaving]);

  const handleSubmitClick = async () => {
    setIsSaving(true);
    // Retrieving the original task details from popup data
    const originalTask = cloneDeep(popupData.item);

    // Preparing the files array to be added to the updated task
    let filesRefArray = [];
    let newCommentRef = "";

    try {
      // Uploading files if there are any to add
      if (filesToAdd.length > 0) {
        const filesResponse = await postFilesPromise(filesToAdd);
        filesRefArray = filesResponse.map((f) => f.reference);
      }
      // Posting a comment if it's not empty
      if (comment !== "") {
        const newComment = {
          content: comment,
          association: originalTask.reference,
          author: currentUser.reference,
          files: [filesRefArray],
        };
        // Posting the comment and retrieving the reference
        const commentResponse = await CommentAPI.post(newComment);
        newCommentRef = commentResponse?.data?.reference || "";
      }

      // Setting the closing details of the task
      const updatedTask = {
        ...popupData.item,
        steps:
          !initialState.steps && completeStatus.steps
            ? updatedSteps(popupData.item)
            : popupData?.item?.steps,
        assets:
          !initialState.assets && completeStatus.assets
            ? updatedResourceItems(popupData.item, "assets")
            : popupData?.item?.assets,
        spaces:
          !initialState.spaces && completeStatus.spaces
            ? updatedResourceItems(popupData.item, "spaces")
            : popupData?.item?.spaces,
        duration: { typeOfDuration: timeUnit.value, value: duration },
        status: "done",
        closing: {
          comment: newCommentRef,
          files: filesRefArray,
          closedBy: currentUser.reference,
          date: moment().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        },
      };

      const editParams = {
        originalItem: originalTask,
        editedItem: updatedTask,
      };

      if (!originalTask.recurrence) {
        const response = await editInCalendar({
          args: editParams,
          operation: "$non-recurring",
        });

        editTaskList(
          {
            args: {
              originalItem: originalTask,
              editedItem: response.data,
            },
            operation: "$non-recurring",
          },
          false
        );

        popupData.handleCleanExit(response);
      } else {
        const response = await editInCalendar({
          args: editParams,
          operation: "$single",
        });

        editTaskList(
          {
            args: {
              originalItem: originalTask,
              editedItem: response.data,
            },
            operation: "$single",
          },
          false
        );

        // update tasks history
        queryClient.invalidateQueries(taskKeys.history(originalTask.id));

        popupData.handleCleanExit(response);
      }

      toastMessage("Task completed successfully");

      closePopup();
    } catch (error) {
      console.error(error);
      // Display an error message in case of any exceptions
      toastError("Unable to complete task. Please try again later.");
    }

    setIsSaving(false);
  };

  return {
    name,
    duration,
    timeUnit,
    comment,
    filesToAdd,
    handleRemoveAttachment,
    handleFilesAdded,
    setFilesToAdd,
    handleSubmitClick,
    setComment,
    setTimeUnit,
    handleDurationChange,
    setCompleteStatus,
    completeStatus,
    initialState,
    checkBoxText,
    isSaving,
    disableSubmit,
  };
};

export default useMarkCompletePopupData;
